//UserDashboard.js
import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  RefreshCw,
  Trash2,
  CreditCard,
  AlertCircle,
  Settings,
  X,
} from "lucide-react";
import { useAuth } from "./AuthContext";
import api from "./api";
import "./UserDashboard.css";
import ProgressBar from "./ProgressBar";
import {
  analysisProgressFromRepo,
  deriveAnalysisStage,
} from "./analysisProgress";
import {
  PRIVATE_REPO_UI,
  buildPrivateRepoEstimatePayload,
  buildPrivateRepoSubmitPayload,
  canShowPrivateAnalyzeButton,
  findExistingUserRepo,
  getGitHubPrivateRepoRowState,
  mapPrivateRepoSubmitError,
  pickGitHubManageUrl,
  privateRepoAnalyzeKey,
  summarizeInstallationRepoFetch,
} from "./githubPrivateRepo";
import SlackConnectionPanel from "./SlackConnectionPanel";
import RepoComparisonPanel from "./RepoComparisonPanel";
import WorkspaceTeamPanel from "./WorkspaceTeamPanel";
import { groupReposIntoLogical } from "./logicalRepoGrouping";
import RepositoryAnalysisConfirmation from "./RepositoryAnalysisConfirmation";
import {
  canManageIntegrations,
  canMutateRepos,
  canDeleteRepos,
  VIEWER_READ_ONLY_MESSAGE,
} from "./orgPermissions";

const PROFILE_POLL_MS = 20000; // Was 5s — overlapped slow /profile responses and saturated the API

/** Must match server fallbacks: `queries.ts` and `User` schema use LLM_MODEL_LITE ?? gpt-4o-mini */
const DEFAULT_LLM_MODEL =
  process.env.REACT_APP_DEFAULT_MODEL || "gpt-4o-mini";

const LLM_MODEL_OPTIONS = [
  { value: "gpt-4o-mini", label: "GPT-4o Mini (default)" },
  { value: "gpt-3.5-turbo", label: "GPT-3.5 Turbo" },
  { value: "gpt-4o", label: "GPT-4o" },
];

/** User-facing GitHub App dashboard copy (never surface env flags or server internals). */
const GITHUB_APP_COPY = {
  disconnected: {
    title: "GitHub private repositories",
    body: "Connect GitHub to view the private repositories you’ve selected for RenderRepo.",
    previewNote:
      "Private repo analysis is not available yet, but you can connect your account now so setup is ready when it launches.",
    button: "Connect GitHub",
  },
  connected: {
    title: "GitHub connected",
    body: "RenderRepo can see the repositories you selected during installation.",
    previewNote: "Private repo analysis is coming soon.",
  },
  error: {
    message:
      "We couldn’t load your GitHub connection. Try refreshing, or reconnect GitHub.",
    button: "Connect GitHub",
  },
  installSuccess: "GitHub connected successfully.",
  installCancelled:
    "GitHub connection was cancelled or didn’t complete.",
  connectUnavailable:
    "GitHub connection isn’t available right now. Please try again later.",
};

function sanitizeGitHubConnectError(serverMessage) {
  if (!serverMessage || typeof serverMessage !== "string") {
    return GITHUB_APP_COPY.connectUnavailable;
  }
  if (/GITHUB_APP|PRIVATE_REPO|_ENABLED|server flag/i.test(serverMessage)) {
    return GITHUB_APP_COPY.connectUnavailable;
  }
  return serverMessage;
}

export function isAllowedGitHubUrl(url) {
  try {
    const parsed = new URL(url);
    return (
      parsed.protocol === "https:" &&
      parsed.hostname === "github.com" &&
      parsed.pathname.startsWith("/apps/")
    );
  } catch {
    return false;
  }
}

export const UserDashboard = ({ onSelectRepo }) => {
  const { user, activeOrganization } = useAuth();
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const profileFetchInFlight = useRef(false);
  const [showPurchase, setShowPurchase] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [githubInstallMessage, setGithubInstallMessage] = useState("");
  const [githubInstallations, setGithubInstallations] = useState([]);
  const [githubConnectLoading, setGithubConnectLoading] = useState(false);
  const [githubLoadError, setGithubLoadError] = useState(false);
  const [installationReposById, setInstallationReposById] = useState({});
  const [installationReposLoading, setInstallationReposLoading] = useState(false);
  const [githubRefreshLoading, setGithubRefreshLoading] = useState(false);
  const [githubRefreshMessage, setGithubRefreshMessage] = useState("");
  const [githubRefreshError, setGithubRefreshError] = useState("");
  const [githubCapabilities, setGithubCapabilities] = useState(null);
  const [privateRepoAnalyzeState, setPrivateRepoAnalyzeState] = useState({});
  const privateAnalyzeInFlight = useRef(new Set());
  const [settings, setSettings] = useState({
    defaultLlmModel:
      user?.settings?.defaultLlmModel?.trim() || DEFAULT_LLM_MODEL,
    defaultMaxFileSize: user?.settings?.defaultMaxFileSize || 256 * 1024,
  });

  const permissionUser = {
    ...user,
    organizationRole:
      activeOrganization?.role ||
      userProfile?.organizationRole ||
      user?.organizationRole ||
      "owner",
  };
  const integrationsAllowed = canManageIntegrations(permissionUser);
  const mutateAllowed = canMutateRepos(permissionUser);
  const deleteAllowed = canDeleteRepos(permissionUser);
  const isViewer = permissionUser.organizationRole === "viewer";

  const fetchGitHubCapabilities = useCallback(async () => {
    try {
      const res = await api.get("/github-app/capabilities");
      setGithubCapabilities(res.data || null);
    } catch {
      setGithubCapabilities(null);
    }
  }, []);

  const fetchInstallationRepos = useCallback(async (installations) => {
    if (!installations?.length) {
      setInstallationReposById({});
      return { hadErrors: false, hadSuccess: false };
    }
    setInstallationReposLoading(true);
    const results = {};
    await Promise.all(
      installations.map(async (inst) => {
        const id = inst.installationId;
        try {
          const res = await api.get(`/github-app/installations/${id}/repos`);
          results[id] = {
            repos: res.data.repositories || [],
            error: null,
          };
        } catch {
          results[id] = { repos: [], error: true };
        }
      })
    );
    setInstallationReposById(results);
    setInstallationReposLoading(false);
    return summarizeInstallationRepoFetch(results);
  }, []);

  const fetchGitHubInstallations = useCallback(async () => {
    try {
      setGithubLoadError(false);
      const res = await api.get("/github-app/installations");
      const installations = res.data.installations || [];
      setGithubInstallations(installations);
      await fetchInstallationRepos(installations);
    } catch {
      setGithubInstallations([]);
      setInstallationReposById({});
      setGithubLoadError(true);
    }
  }, [fetchInstallationRepos]);

  const fetchUserData = useCallback(async () => {
    if (profileFetchInFlight.current) return;
    profileFetchInFlight.current = true;
    try {
      const response = await api.get("/users/profile");
      const repos = response.data.repos || [];

      const sortedRepos = [...repos].sort((a, b) => {
        if (a.status === "processing" && b.status !== "processing") return -1;
        if (b.status === "processing" && a.status !== "processing") return 1;
        return 0;
      });

      setUserProfile({
        ...response.data,
        repos: sortedRepos,
      });
      const s = response.data.settings || {};
      const modelFromServer = s.defaultLlmModel?.trim?.() || s.defaultLlmModel;
      setSettings((prev) => ({
        ...prev,
        defaultLlmModel: modelFromServer || DEFAULT_LLM_MODEL,
        ...(typeof s.defaultMaxFileSize === "number"
          ? { defaultMaxFileSize: s.defaultMaxFileSize }
          : {}),
      }));
      setLoading(false);
    } catch (err) {
      console.error("Failed to fetch user data:", err);
      setError("Failed to load user data");
      setLoading(false);
    } finally {
      profileFetchInFlight.current = false;
    }
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const gh = params.get("github_installation");
    if (gh === "error") {
      setGithubInstallMessage(GITHUB_APP_COPY.installCancelled);
      window.history.replaceState({}, "", window.location.pathname);
    } else if (gh === "success") {
      setGithubInstallMessage(GITHUB_APP_COPY.installSuccess);
      window.history.replaceState({}, "", window.location.pathname);
    }
    fetchUserData();
    fetchGitHubInstallations();
    fetchGitHubCapabilities();
    const pollInterval = setInterval(fetchUserData, PROFILE_POLL_MS);
    return () => clearInterval(pollInterval);
  }, [
    fetchUserData,
    fetchGitHubInstallations,
    fetchGitHubCapabilities,
    activeOrganization?.organizationId,
  ]);

  const privateAnalysisPayload = (installationId, repo, quoteToken) => ({
    owner: repo.owner,
    name: repo.name,
    githubInstallationId: installationId,
    maxFileSize: settings.defaultMaxFileSize,
    llmModel: settings.defaultLlmModel,
    quoteToken,
  });

  const privateAnalysisErrorData = (err) =>
    err?.originalError?.response?.data ??
    err?.response?.data ?? { code: err?.code, error: err?.message };

  const handlePreviewPrivateRepo = async (installationId, repo) => {
    if (!repo?.private) return;

    const analyzeKey = privateRepoAnalyzeKey(installationId, repo.fullName);
    if (privateAnalyzeInFlight.current.has(analyzeKey)) return;

    privateAnalyzeInFlight.current.add(analyzeKey);
    setPrivateRepoAnalyzeState((prev) => ({
      ...prev,
      [analyzeKey]: { loading: true, message: null, error: null },
    }));

    try {
      const payload = buildPrivateRepoEstimatePayload(
        privateAnalysisPayload(installationId, repo)
      );
      const response = await api.post("/repos/estimate", payload);
      setPrivateRepoAnalyzeState((prev) => ({
        ...prev,
        [analyzeKey]: {
          loading: false,
          estimate: response.data.estimate,
          quoteToken: response.data.quoteToken,
          expiresAt: response.data.expiresAt,
          message: null,
          error: null,
        },
      }));
    } catch (err) {
      setPrivateRepoAnalyzeState((prev) => ({
        ...prev,
        [analyzeKey]: {
          loading: false,
          message: null,
          error: mapPrivateRepoSubmitError(privateAnalysisErrorData(err)),
        },
      }));
    } finally {
      privateAnalyzeInFlight.current.delete(analyzeKey);
    }
  };

  const handleConfirmPrivateRepo = async (installationId, repo) => {
    const analyzeKey = privateRepoAnalyzeKey(installationId, repo.fullName);
    const current = privateRepoAnalyzeState[analyzeKey];
    if (!current?.quoteToken || privateAnalyzeInFlight.current.has(analyzeKey)) return;

    privateAnalyzeInFlight.current.add(analyzeKey);
    setPrivateRepoAnalyzeState((prev) => ({
      ...prev,
      [analyzeKey]: { ...prev[analyzeKey], loading: true, error: null },
    }));
    try {
      const payload = buildPrivateRepoSubmitPayload(
        privateAnalysisPayload(installationId, repo, current.quoteToken)
      );
      await api.post("/repos", payload);
      await fetchUserData();
      setPrivateRepoAnalyzeState((prev) => ({
        ...prev,
        [analyzeKey]: {
          loading: false,
          message: PRIVATE_REPO_UI.analyze.success,
          error: null,
        },
      }));
    } catch (err) {
      const responseData = privateAnalysisErrorData(err);
      const quoteMustRefresh = [
        "QUOTE_EXPIRED",
        "QUOTE_INVALID",
        "QUOTE_IDENTITY_MISMATCH",
        "ESTIMATE_CHANGED",
      ].includes(responseData?.code);
      setPrivateRepoAnalyzeState((prev) => ({
        ...prev,
        [analyzeKey]: {
          ...(quoteMustRefresh ? {} : prev[analyzeKey]),
          loading: false,
          message: null,
          error: mapPrivateRepoSubmitError(responseData),
        },
      }));
    } finally {
      privateAnalyzeInFlight.current.delete(analyzeKey);
    }
  };

  const cancelPrivateRepoPreview = (installationId, repo) => {
    const analyzeKey = privateRepoAnalyzeKey(installationId, repo.fullName);
    setPrivateRepoAnalyzeState((prev) => ({
      ...prev,
      [analyzeKey]: {},
    }));
  };

  const handleConnectGitHubApp = async () => {
    setGithubConnectLoading(true);
    setError("");
    try {
      const res = await api.get("/github-app/install-url");
      const url = res.data?.installUrl;
      if (url && isAllowedGitHubUrl(url)) {
        window.location.href = url;
        return;
      }
      setError("GitHub connection isn’t available right now. Please try again later.");
    } catch (err) {
      setError(sanitizeGitHubConnectError(err?.response?.data?.error));
    } finally {
      setGithubConnectLoading(false);
    }
  };

  const handleManageGitHubRepos = (manageUrl) => {
    if (manageUrl) {
      window.open(manageUrl, "_blank", "noopener,noreferrer");
      return;
    }
    handleConnectGitHubApp();
  };

  const handleRefreshGitHubRepos = async () => {
    setGithubRefreshLoading(true);
    setGithubRefreshMessage("");
    setGithubRefreshError("");
    try {
      setGithubLoadError(false);
      const res = await api.get("/github-app/installations");
      const installations = res.data.installations || [];
      setGithubInstallations(installations);
      const fetchSummary = await fetchInstallationRepos(installations);
      if (fetchSummary.hadErrors) {
        setGithubRefreshError(PRIVATE_REPO_UI.connected.installReposError);
      } else {
        setGithubRefreshMessage(PRIVATE_REPO_UI.connected.refreshSuccess);
      }
    } catch {
      setGithubInstallations([]);
      setInstallationReposById({});
      setGithubLoadError(true);
      setGithubRefreshError(PRIVATE_REPO_UI.connected.refreshError);
    } finally {
      setGithubRefreshLoading(false);
    }
  };

  const handleRefreshRepo = async (repoId) => {
    if (
      !window.confirm(
        "Are you sure you want to refresh this repository? This will use additional credits."
      )
    ) {
      return;
    }

    try {
      const response = await api.post(`/users/repos/${repoId}/refresh`);
      if (response.data.repo) {
        onSelectRepo(response.data.repo);
      }
      await fetchUserData();
    } catch (err) {
      setError(err.response?.data?.error || "Failed to refresh repository");
    }
  };

  const handleDeleteRepo = async (repoId) => {
    if (!window.confirm("Are you sure you want to delete this repository?")) {
      return;
    }

    try {
      await api.delete(`/users/repos/${repoId}`);
      await fetchUserData();
    } catch (err) {
      setError("Failed to delete repository");
    }
  };

  const initiatePurchase = async (credits) => {
    try {
      const response = await api.post("/users/credits/purchase", { credits });
      window.location.href = response.data.url;
    } catch (err) {
      setError("Failed to initiate purchase");
    }
  };

  const handleSettingsSubmit = async (e) => {
    e.preventDefault();
    setError("");
    try {
      await api.patch("/auth/settings", settings);
      setShowSettings(false);
      await fetchUserData();
    } catch (err) {
      setError(err.response?.data?.error || "Failed to update settings");
      console.error("Failed to update settings:", err);
    }
  };

  if (loading) {
    return <div className="loading">Loading dashboard...</div>;
  }

  const githubConnected =
    !githubLoadError && githubInstallations.length > 0;
  const githubDisconnected = !githubLoadError && !githubConnected;
  const privateAnalyzeEnabled = canShowPrivateAnalyzeButton(githubCapabilities);
  const userRepos = (userProfile?.repos ?? []).filter((r) => !r.isStarter);
  const githubSectionCopy = githubLoadError
    ? null
    : githubConnected
      ? PRIVATE_REPO_UI.connected
      : GITHUB_APP_COPY.disconnected;
  const connectedPreviewNote = privateAnalyzeEnabled
    ? PRIVATE_REPO_UI.connected.previewNoteEnabled
    : PRIVATE_REPO_UI.connected.previewNoteDisabled;
  const githubManageUrl = pickGitHubManageUrl(githubInstallations);

  return (
    <div className="dashboard-container">
      <div className="dashboard-header">
        <div className="credit-info">
          <h2>Your Credits</h2>
          <div className="credit-stats">
            <div>
              <p>Available Credits</p>
              <h3>{(userProfile?.usage?.availableCredits || 0).toLocaleString()}</h3>
            </div>
            <div>
              <p>Total Used</p>
              <h3>{(userProfile?.usage?.totalCreditsUsed || 0).toLocaleString()}</h3>
            </div>
          </div>
          <p className="credit-microcopy">
            Repository processing uses credits. Queries are free during
            pre-launch.
          </p>
          {userProfile?.dealCode && (
            <div className="deal-code-info">
              <span className="deal-badge">Deal: {userProfile.dealCode}</span>
            </div>
          )}
        </div>

        <div className="dashboard-actions">
          <button
            className="purchase-btn"
            onClick={() => setShowPurchase(true)}
          >
            <CreditCard size={18} />
            Purchase Credits
          </button>
          <button
            className="settings-btn"
            onClick={() => {
              const s = userProfile?.settings || {};
              const m = s.defaultLlmModel?.trim?.() || s.defaultLlmModel;
              setSettings((prev) => ({
                ...prev,
                defaultLlmModel: m || DEFAULT_LLM_MODEL,
                ...(typeof s.defaultMaxFileSize === "number"
                  ? { defaultMaxFileSize: s.defaultMaxFileSize }
                  : {}),
              }));
              setShowSettings(true);
            }}
          >
            <Settings size={18} />
            Settings
          </button>
        </div>
      </div>

      <div className="github-app-section repositories-section">
        {githubLoadError ? (
          <>
            <h2>{GITHUB_APP_COPY.disconnected.title}</h2>
            <p className="github-section-error">{GITHUB_APP_COPY.error.message}</p>
            <button
              type="button"
              className="purchase-btn"
              onClick={() => {
                setGithubLoadError(false);
                fetchGitHubInstallations();
                fetchGitHubCapabilities();
              }}
            >
              Refresh
            </button>
            <button
              type="button"
              className="purchase-btn github-connect-btn"
              onClick={handleConnectGitHubApp}
              disabled={githubConnectLoading || !integrationsAllowed}
            >
              {githubConnectLoading ? "Loading…" : GITHUB_APP_COPY.error.button}
            </button>
          </>
        ) : (
          <>
            <h2>{githubSectionCopy.title}</h2>
            <p className="starter-repos-desc">{githubSectionCopy.body}</p>
            <p className="github-preview-note">{connectedPreviewNote}</p>
            {githubInstallMessage && (
              <p className="github-install-message">{githubInstallMessage}</p>
            )}
            {githubDisconnected && integrationsAllowed && (
              <button
                type="button"
                className="purchase-btn github-connect-btn"
                onClick={handleConnectGitHubApp}
                disabled={githubConnectLoading}
              >
                {githubConnectLoading
                  ? "Loading…"
                  : GITHUB_APP_COPY.disconnected.button}
              </button>
            )}
            {!integrationsAllowed && (
              <p className="github-preview-note">{VIEWER_READ_ONLY_MESSAGE}</p>
            )}
            {githubConnected && integrationsAllowed && (
              <>
                <div className="github-manage-actions">
                  <button
                    type="button"
                    className="secondary-button github-manage-btn"
                    onClick={() => handleManageGitHubRepos(githubManageUrl)}
                  >
                    {PRIVATE_REPO_UI.connected.manageButton}
                  </button>
                  <button
                    type="button"
                    className="secondary-button github-refresh-btn"
                    onClick={handleRefreshGitHubRepos}
                    disabled={githubRefreshLoading || installationReposLoading}
                  >
                    <RefreshCw
                      size={16}
                      className={
                        githubRefreshLoading || installationReposLoading
                          ? "github-refresh-icon--spinning"
                          : undefined
                      }
                    />
                    {githubRefreshLoading || installationReposLoading
                      ? PRIVATE_REPO_UI.connected.refreshLoading
                      : PRIVATE_REPO_UI.connected.refreshButton}
                  </button>
                </div>
                <p className="github-manage-hint">
                  {PRIVATE_REPO_UI.connected.manageHint}
                </p>
                {githubRefreshMessage && (
                  <p className="github-repo-status-note success">
                    {githubRefreshMessage}
                  </p>
                )}
                {githubRefreshError && (
                  <p className="github-section-error">{githubRefreshError}</p>
                )}
                <p className="github-access-note">
                  {PRIVATE_REPO_UI.connected.accessNote}
                </p>
                <ul className="github-installations-list">
                {githubInstallations.map((inst) => {
                  const repoState = installationReposById[inst.installationId];
                  const selectedRepos = repoState?.repos ?? [];
                  return (
                    <li key={inst.id} className="github-installation-item">
                      <div className="github-installation-account">
                        <strong>{inst.accountLogin}</strong>
                        {inst.accountType ? ` (${inst.accountType})` : ""}
                      </div>
                      {installationReposLoading && !repoState && (
                        <p className="github-repos-loading">Loading selected repositories…</p>
                      )}
                      {repoState?.error && (
                        <p className="github-section-error">
                          {PRIVATE_REPO_UI.connected.installReposError}
                        </p>
                      )}
                      {repoState && !repoState.error && selectedRepos.length > 0 && (
                        <ul className="github-selected-repos-list">
                          {selectedRepos.map((repo) => {
                            const analyzeKey = privateRepoAnalyzeKey(
                              inst.installationId,
                              repo.fullName
                            );
                            const analyzeState =
                              privateRepoAnalyzeState[analyzeKey] ?? {};
                            const existingRepo = findExistingUserRepo(
                              userRepos,
                              repo.owner,
                              repo.name
                            );
                            const rowState = getGitHubPrivateRepoRowState({
                              existingRepo,
                              isPrivate: repo.private,
                              privateAnalyzeEnabled,
                              analyzeLoading: analyzeState.loading,
                            });

                            const showRowAction =
                              rowState.kind === "already_analyzed" ||
                              rowState.kind === "analyze" ||
                              rowState.kind === "failed" ||
                              rowState.kind === "processing";

                            return (
                              <li
                                key={repo.id}
                                className="github-selected-repo-item"
                                data-testid={`github-repo-row-${repo.fullName}`}
                              >
                                <div className="github-selected-repo-primary">
                                  <span className="github-selected-repo-name">
                                    {repo.fullName}
                                  </span>
                                  {rowState.statusLabel && (
                                    <span className="github-repo-inline-status">
                                      {rowState.statusLabel}
                                    </span>
                                  )}
                                  {rowState.hint && (
                                    <span className="github-repo-row-hint">
                                      {rowState.hint}
                                    </span>
                                  )}
                                  {analyzeState.message && (
                                    <p className="github-repo-status-note success">
                                      {analyzeState.message}
                                    </p>
                                  )}
                                  {analyzeState.error && (
                                    <p className="github-section-error">
                                      {analyzeState.error}
                                    </p>
                                  )}
                                </div>
                                {showRowAction && (
                                  <div className="github-selected-repo-action">
                                    {rowState.kind === "already_analyzed" ? (
                                      <button
                                        type="button"
                                        className="secondary-button github-open-repo-btn"
                                        onClick={() => onSelectRepo(existingRepo)}
                                      >
                                        {rowState.actionLabel}
                                      </button>
                                    ) : mutateAllowed ? (
                                      <button
                                        type="button"
                                        className={`${
                                          rowState.kind === "failed"
                                            ? "secondary-button github-retry-btn"
                                            : "purchase-btn github-analyze-btn"
                                        }`}
                                        disabled={rowState.actionDisabled}
                                        onClick={() =>
                                          handlePreviewPrivateRepo(
                                            inst.installationId,
                                            repo
                                          )
                                        }
                                      >
                                        {rowState.actionLabel}
                                      </button>
                                    ) : null}
                                  </div>
                                )}
                                {analyzeState.estimate && (
                                  <div className="github-repo-analysis-confirmation">
                                    <RepositoryAnalysisConfirmation
                                      compact
                                      estimate={analyzeState.estimate}
                                      loading={analyzeState.loading}
                                      onConfirm={() =>
                                        handleConfirmPrivateRepo(
                                          inst.installationId,
                                          repo
                                        )
                                      }
                                      onPurchase={() => setShowPurchase(true)}
                                      onCancel={() =>
                                        cancelPrivateRepoPreview(
                                          inst.installationId,
                                          repo
                                        )
                                      }
                                    />
                                  </div>
                                )}
                              </li>
                            );
                          })}
                        </ul>
                      )}
                      {repoState && !repoState.error && selectedRepos.length === 0 && (
                        <p className="github-repos-empty-note">
                          {PRIVATE_REPO_UI.connected.reposEmpty}
                        </p>
                      )}
                      {inst.manageUrl && githubInstallations.length > 1 && (
                        <button
                          type="button"
                          className="secondary-button github-manage-account-btn"
                          onClick={() => handleManageGitHubRepos(inst.manageUrl)}
                        >
                          {PRIVATE_REPO_UI.connected.manageButton}
                        </button>
                      )}
                    </li>
                  );
                })}
              </ul>
              </>
            )}
          </>
        )}
      </div>

      {error && (
        <div className="error-message">
          <AlertCircle size={16} />
          <span>{error}</span>
        </div>
      )}

      {(() => {
        const repos = userProfile?.repos ?? [];
        const userRepos = repos.filter((r) => !r.isStarter);
        const starterRepos = repos.filter((r) => r.isStarter);

        const logicalUserRepos = groupReposIntoLogical(userRepos);
        const logicalStarterRepos = groupReposIntoLogical(starterRepos);

        const renderRepoCard = (group) => {
          const repo = group.canonicalRepo;
          return (
            <div key={repo._id} className="repo-card">
              <div className="repo-info" onClick={() => onSelectRepo(repo)}>
                <h3>
                  {repo.owner}/{repo.name}
                </h3>
                <span className={`status-badge ${repo.status}`}>
                  {repo.status}
                </span>
                {repo.isStarter && (
                  <span className="status-badge starter" title="Demo repository (read-only)">
                    Starter
                  </span>
                )}
                {group.branchCount > 0 && (
                  <span className="status-badge branches" title={group.branches.map((b) => b.branch).join(", ")}>
                    {group.branchCount} {group.branchCount === 1 ? "branch" : "branches"}
                  </span>
                )}
                {repo.status === "processing" ? (
                  <>
                    <p className="repo-progress">
                      Files processed: {repo.processedFiles?.length || 0} /{" "}
                      {repo.processableFiles || 0}
                    </p>
                    {repo.analysisCoverage?.fileCapApplied && (
                      <p className="repo-coverage-note" title={`${repo.analysisCoverage.eligibleFileCount ?? repo.analysisCoverage.eligibleBeforeCap ?? 0} eligible files; analysis limited to ${repo.analysisCoverage.selectedFileCount ?? repo.processableFiles ?? 0}`}>
                        Analysis capped at {repo.analysisCoverage.selectedFileCount ?? repo.processableFiles ?? 0} files
                      </p>
                    )}
                    {(repo.analysisCoverage?.failedFileCount ?? 0) > 0 && (
                      <p className="repo-failed-note">
                        {repo.analysisCoverage.failedFileCount} file{repo.analysisCoverage.failedFileCount !== 1 ? "s" : ""} failed
                      </p>
                    )}
                    <ProgressBar
                      processed={repo.processedFiles?.length || 0}
                      total={repo.processableFiles || 0}
                      compact={true}
                      showEstimate={true}
                      stage={deriveAnalysisStage({
                        status: repo.status,
                        finalizeStatus: repo.finalizeStatus,
                        progress: analysisProgressFromRepo(repo),
                      })}
                    />
                  </>
                ) : (
                  <>
                    <p className="repo-progress">
                      Files processed: {repo.processedFiles?.length || 0} /{" "}
                      {repo.processableFiles || 0}
                    </p>
                    {repo.analysisCoverage?.fileCapApplied && (
                      <p className="repo-coverage-note" title={`${repo.analysisCoverage.eligibleFileCount ?? repo.analysisCoverage.eligibleBeforeCap ?? 0} eligible; ${repo.analysisCoverage.selectedFileCount ?? repo.processableFiles ?? 0} analyzed`}>
                        Analysis capped at {repo.analysisCoverage.selectedFileCount ?? repo.processableFiles ?? 0} files
                      </p>
                    )}
                    {(repo.analysisCoverage?.failedFileCount ?? 0) > 0 && (
                      <p className="repo-failed-note">
                        {repo.analysisCoverage.failedFileCount} file{repo.analysisCoverage.failedFileCount !== 1 ? "s" : ""} failed
                      </p>
                    )}
                  </>
                )}
                {group.branchCount > 1 && (
                  <div className="repo-branch-list">
                    {group.branches.map((b) => (
                      <span key={b.repoId} className={`branch-tag ${b.status}`}>
                        {b.branch}
                      </span>
                    ))}
                  </div>
                )}
              </div>
              {!repo.isStarter && (mutateAllowed || deleteAllowed) && (
                <div className="repo-actions">
                  {mutateAllowed ? (
                    <button
                      className="icon-btn refresh"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRefreshRepo(repo._id);
                      }}
                      title="Refresh Analysis"
                    >
                      <RefreshCw size={16} />
                    </button>
                  ) : null}
                  {deleteAllowed ? (
                    <button
                      className="icon-btn delete"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteRepo(repo._id);
                      }}
                      title="Delete Repository"
                    >
                      <Trash2 size={16} />
                    </button>
                  ) : null}
                </div>
              )}
            </div>
          );
        };
        return (
          <>
            <div className="repositories-section">
              <h2>Your Repositories</h2>
              <div className="repo-grid">
                {logicalUserRepos.map(renderRepoCard)}
                {logicalUserRepos.length === 0 && (
                  <div className="no-repos">
                    <p>
                      No repositories analyzed yet. Click "New Repository" to add one.
                      {logicalStarterRepos.length > 0 && " Or try a starter repo below."}
                    </p>
                  </div>
                )}
              </div>
            </div>
            {logicalStarterRepos.length > 0 && (
              <div className="repositories-section starter-repos-section">
                <h2>Starter Repositories</h2>
                <p className="starter-repos-desc">Pre-analyzed demos you can open and query right away—no credits needed.</p>
                <div className="repo-grid">
                  {logicalStarterRepos.map(renderRepoCard)}
                </div>
              </div>
            )}
          </>
        );
      })()}

      <RepoComparisonPanel
        repos={(userProfile?.repos ?? []).filter((r) => r.status === "completed")}
        readOnly={!mutateAllowed}
      />

      {isViewer ? (
        <p className="starter-repos-desc">{VIEWER_READ_ONLY_MESSAGE}</p>
      ) : null}

      <WorkspaceTeamPanel
        organizationId={activeOrganization?.organizationId}
        workspaceName={activeOrganization?.name}
      />

      {/* Settings Modal */}
      {showSettings && (
        <div className="modal">
          <div className="modal-content">
            <div className="modal-header">
              <h2>User Settings</h2>
              <button
                className="modal-close"
                onClick={() => setShowSettings(false)}
                aria-label="Close"
              >
                <X size={20} />
              </button>
            </div>
            <form onSubmit={handleSettingsSubmit} className="settings-form">
              <div className="form-group">
                <label htmlFor="llmModel">Default LLM Model</label>
                <select
                  id="llmModel"
                  value={
                    LLM_MODEL_OPTIONS.some(
                      (o) => o.value === settings.defaultLlmModel
                    )
                      ? settings.defaultLlmModel
                      : settings.defaultLlmModel
                        ? `__other__:${settings.defaultLlmModel}`
                        : DEFAULT_LLM_MODEL
                  }
                  onChange={(e) => {
                    const v = e.target.value;
                    if (v.startsWith("__other__:")) return;
                    setSettings((s) => ({ ...s, defaultLlmModel: v }));
                  }}
                  className="form-input"
                >
                  {LLM_MODEL_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                  {settings.defaultLlmModel &&
                    !LLM_MODEL_OPTIONS.some(
                      (o) => o.value === settings.defaultLlmModel
                    ) && (
                      <option
                        value={`__other__:${settings.defaultLlmModel}`}
                      >
                        Current: {settings.defaultLlmModel}
                      </option>
                    )}
                </select>
                <p className="settings-hint text-sm text-gray-500">
                  Used for Query Repository answers and Feature Builder LLM
                  steps. Server default is GPT-4o Mini if unset.
                </p>
              </div>

              <div className="form-group">
                <label htmlFor="maxFileSize">Max File Size (MB)</label>
                <input
                  type="number"
                  id="maxFileSize"
                  min="0"
                  max="1"
                  step="0.01"
                  value={settings.defaultMaxFileSize / (1024 * 1024)}
                  onChange={(e) =>
                    setSettings((s) => ({
                      ...s,
                      defaultMaxFileSize: e.target.value * 1024 * 1024,
                    }))
                  }
                  className="form-input"
                />
                <div className="text-sm text-gray-500">
                  Maximum allowed size: 1MB
                </div>
              </div>

              <SlackConnectionPanel />

              {error && (
                <div className="error-message">
                  <AlertCircle size={16} />
                  <span>{error}</span>
                </div>
              )}

              <div className="form-actions">
                <button type="submit" className="primary-button">
                  Save Changes
                </button>
                <button
                  type="button"
                  onClick={() => setShowSettings(false)}
                  className="secondary-button"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Purchase Credits Modal */}
      {showPurchase && (
        <div className="modal">
          <div className="modal-content">
            <div className="modal-header">
              <h2>Purchase Credits</h2>
              <button
                className="modal-close"
                onClick={() => setShowPurchase(false)}
                aria-label="Close"
              >
                <X size={20} />
              </button>
            </div>
            <div className="purchase-options">
              <button onClick={() => initiatePurchase(10000)}>
                <div>
                  <strong>10,000 Credits</strong>
                  <div className="text-sm text-gray-500">
                    Perfect for small projects
                  </div>
                </div>
                <div className="text-lg font-semibold">$10</div>
              </button>
              <button onClick={() => initiatePurchase(20000)}>
                <div>
                  <strong>20,000 Credits</strong>
                  <div className="text-sm text-gray-500">Most popular</div>
                </div>
                <div className="text-lg font-semibold">$20</div>
              </button>
              <button onClick={() => initiatePurchase(30000)}>
                <div>
                  <strong>30,000 Credits</strong>
                  <div className="text-sm text-gray-500">Best value</div>
                </div>
                <div className="text-lg font-semibold">$30</div>
              </button>
            </div>
            <div className="text-sm text-gray-500 mt-4">
              1 credit = 1,000 tokens. Unused credits never expire.
            </div>
          </div>
        </div>
      )}

    </div>
  );
};
